import { FC, ComponentProps } from 'react';
import {
    Box,
    BoxProps,
    FormControl,
    FormHelperText,
    InputLabel,
    Select as MuiSelect,
    SelectChangeEvent,
    SxProps,
    Theme,
    useTheme,
} from '@mui/material';
import { ReadOnly } from '../ReadOnly';
import { useFieldsReadonly } from '../../ui/Form/FormControlContext';
import { renderTooltipLabel, ITooltipIconProps, hideTooltipIconWhenLabelNotFloating } from '../Tooltip';

export interface ISelectProps {
    label?: string;
    labelId?: string;
    id?: string;
    value?: string | number | (string | number)[];
    name?: string;
    onChange?: (event: SelectChangeEvent<string | number | (string | number)[]>) => void;
    readonly?: boolean;
    size?: 'small' | 'medium';
    required?: boolean;
    testId?: string;
    tooltip?: ITooltipIconProps;
    disabled?: boolean;
    renderValue?: ComponentProps<typeof MuiSelect>['renderValue'];
    inputLabelSx?: SxProps<Theme>;
    selectContainerSx?: BoxProps['sx'];
    multiple?: boolean;
    error?: boolean;
    helperText?: string;
    displayEmpty?: boolean;
    'aria-label'?: string;
}

const Select: FC<ISelectProps> = ({
    label,
    labelId,
    id,
    value,
    name,
    onChange,
    children,
    readonly,
    size,
    required,
    testId,
    tooltip,
    disabled,
    renderValue,
    inputLabelSx,
    multiple,
    selectContainerSx,
    error,
    helperText,
    displayEmpty,
    'aria-label': ariaLabel,
}) => {
    const theme = useTheme();

    const isReadonly = useFieldsReadonly(readonly);
    if (isReadonly) {
        return (
            <ReadOnly
                testId={testId}
                label={label}
                value={Array.isArray(value) ? value.join(', ') : value}
                tooltip={tooltip}
            />
        );
    }

    const selectLabel = renderTooltipLabel({ label, tooltip });
    const hasVisibleLabel = !!label;
    const hasExternalLabel = !!labelId && !hasVisibleLabel;
    const useAriaLabelWrapper = !hasVisibleLabel && !hasExternalLabel && !!ariaLabel;
    const nativeInputA11yProps = hasExternalLabel
        ? {
              'aria-labelledby': labelId,
              ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
          }
        : useAriaLabelWrapper
        ? { 'aria-label': ariaLabel }
        : undefined;

    const formControl = (
        <FormControl fullWidth={true} sx={hideTooltipIconWhenLabelNotFloating} error={error}>
            {hasVisibleLabel && (
                <InputLabel sx={{ ...inputLabelSx }} id={labelId}>
                    {selectLabel}
                </InputLabel>
            )}
            <MuiSelect
                data-testid={testId}
                variant="outlined"
                label={hasVisibleLabel ? selectLabel : undefined}
                labelId={labelId}
                id={id}
                value={value}
                name={name}
                onChange={onChange}
                fullWidth={true}
                size={size}
                required={required}
                disabled={disabled}
                displayEmpty={displayEmpty}
                renderValue={renderValue}
                multiple={multiple}
                inputProps={nativeInputA11yProps}
                SelectDisplayProps={useAriaLabelWrapper ? { 'aria-label': ariaLabel } : undefined}
                sx={{
                    '.MuiSvgIcon-root': {
                        color: `${theme.palette.text.secondary} !important`,
                    },
                }}
            >
                {children}
            </MuiSelect>
            {helperText && <FormHelperText>{helperText ?? null}</FormHelperText>}
        </FormControl>
    );

    return (
        <Box sx={{ width: 1, ...selectContainerSx }}>
            {useAriaLabelWrapper ? (
                // Wrapping <label> implicitly associates with the MuiSelect hidden native input
                // (the visible combobox div is already labeled via SelectDisplayProps).
                <Box component="label" sx={{ display: 'block', width: 1 }}>
                    <Box
                        component="span"
                        sx={{
                            position: 'absolute',
                            width: '1px',
                            height: '1px',
                            padding: 0,
                            margin: '-1px',
                            overflow: 'hidden',
                            clip: 'rect(0, 0, 0, 0)',
                            whiteSpace: 'nowrap',
                            border: 0,
                        }}
                    >
                        {ariaLabel}
                    </Box>
                    {formControl}
                </Box>
            ) : (
                formControl
            )}
        </Box>
    );
};

export default Select;
